import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globals } from '../../globalfunctions.js';
import { useKeyboardOpen } from '../../hooks/mobile';
import BottomSheet from './BottomSheet';
import './MobileChrome.css';

const TABS = [
  { path: '/portal', label: 'Home', icon: 'fas fa-house', exact: true },
  { path: '/portal/invoices', label: 'Invoices', icon: 'fas fa-file-invoice' },
  { path: '/portal/tickets', label: 'Support', icon: 'fas fa-life-ring' },
  { path: '/portal/quotes', label: 'Quotes', icon: 'fas fa-file-signature' }
];

const MORE = [
  { path: '/portal/subscriptions', label: 'Subscriptions', icon: 'fas fa-rotate' },
  { path: '/portal/domains', label: 'Domains', icon: 'fas fa-globe' },
  { path: '/portal/help', label: 'Help', icon: 'fas fa-circle-question' },
  { path: '/portal/uptime', label: 'Outages', icon: 'fas fa-heart-pulse' },
  { path: '/portal/cpanel', label: 'Resources', icon: 'fas fa-server' },
  { path: '/portal/account', label: 'My Account', icon: 'fas fa-user' }
];

const TITLES = Object.fromEntries([...TABS, ...MORE].map((i) => [i.path, i.label]));

const norm = (p) => String(p || '').toLowerCase().replace(/\/+$/, '') || '/';
const matches = (item, here) => (item.exact ? here === item.path : here === item.path || here.startsWith(`${item.path}/`));

// Detail pages (a ticket, a help article) show a back arrow.
const isDetail = (here) => /^\/portal\/(tickets|help|quotes|invoices)\/.+/.test(here);

const titleFor = (here) => {
  if (TITLES[here]) return TITLES[here];
  if (here.startsWith('/portal/tickets/')) return 'Ticket';
  const parent = [...TABS, ...MORE].find((i) => !i.exact && here.startsWith(`${i.path}/`));
  return parent ? parent.label : 'Portal';
};

// Phone navigation for the customer portal. Same idea as the staff app's
// (MobileChrome): top bar with title/back/account, bottom tabs for the
// sections customers use most, a More sheet for the rest. Light top bar here
// because the portal's own header is light.
export default function PortalMobileChrome({ impersonating, onExitImpersonation, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const keyboardOpen = useKeyboardOpen();
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const here = norm(location.pathname);
  const activeTab = TABS.find((t) => matches(t, here));
  const moreActive = !activeTab && MORE.some((m) => matches(m, here));

  useEffect(() => {
    setMoreOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add('has-mobile-chrome', 'has-portal-chrome');
    return () => document.body.classList.remove('has-mobile-chrome', 'has-portal-chrome');
  }, []);
  useEffect(() => {
    document.body.classList.toggle('kb-open', keyboardOpen);
    return () => document.body.classList.remove('kb-open');
  }, [keyboardOpen]);
  useEffect(() => {
    document.body.classList.toggle('has-impersonation-banner', !!impersonating);
    return () => document.body.classList.remove('has-impersonation-banner');
  }, [impersonating]);

  const name = `${Globals.FirstName || ''} ${Globals.LastName || ''}`.trim() || Globals.UserName;
  const initials = name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  const onTab = (tab) => {
    if (activeTab && activeTab.path === tab.path && here === tab.path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(tab.path);
  };

  return (
    <>
      <header className="m-topbar m-topbar-light">
        {isDetail(here) ? (
          <button type="button" className="m-topbar-btn" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/portal'))} aria-label="Back">
            <i className="fas fa-chevron-left" aria-hidden="true"></i>
          </button>
        ) : (
          <button type="button" className="m-topbar-logo" onClick={() => navigate('/portal')} aria-label="Portal home">
            <img src="/logo192.png" alt="" width="32" height="32" />
          </button>
        )}
        <h1 className="m-topbar-title">{titleFor(here)}</h1>
        <button type="button" className="m-topbar-btn m-avatar-btn" onClick={() => setAccountOpen(true)} aria-label="Account">
          <span className="m-avatar" aria-hidden="true">{initials}</span>
        </button>
      </header>

      {impersonating && (
        <div className="m-banner" role="status">
          <span>Viewing as <strong>{Globals.FirstName || Globals.UserName}</strong> (read-only)</span>
          <button type="button" onClick={onExitImpersonation}>Exit</button>
        </div>
      )}

      <nav className="m-tabbar" aria-label="Main">
        {TABS.map((tab) => {
          const isActive = !!activeTab && activeTab.path === tab.path;
          return (
            <button key={tab.path} type="button" className={`m-tab ${isActive ? 'is-active' : ''}`} aria-current={isActive ? 'page' : undefined} onClick={() => onTab(tab)}>
              <span className="m-tab-icon"><i className={tab.icon} aria-hidden="true"></i></span>
              <span className="m-tab-label">{tab.label}</span>
            </button>
          );
        })}
        <button type="button" className={`m-tab ${moreActive || moreOpen ? 'is-active' : ''}`} onClick={() => setMoreOpen(true)} aria-haspopup="dialog">
          <span className="m-tab-icon"><i className="fas fa-ellipsis" aria-hidden="true"></i></span>
          <span className="m-tab-label">More</span>
        </button>
      </nav>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="m-section">
          {MORE.map((row) => (
            <button key={row.path} type="button" className={`m-row ${matches(row, here) ? 'is-active' : ''}`} onClick={() => navigate(row.path)}>
              <span className="m-row-icon"><i className={row.icon} aria-hidden="true"></i></span>
              <span className="m-row-label">{row.label}</span>
              <i className="fas fa-chevron-right m-row-chevron" aria-hidden="true"></i>
            </button>
          ))}
        </div>
        <div className="m-section">
          <button type="button" className="m-row m-row-danger" onClick={onLogout}>
            <span className="m-row-icon"><i className="fas fa-right-from-bracket" aria-hidden="true"></i></span>
            <span className="m-row-label">Log out</span>
          </button>
        </div>
      </BottomSheet>

      <BottomSheet open={accountOpen} onClose={() => setAccountOpen(false)} label="Account">
        <div className="m-account">
          <span className="m-avatar m-avatar-lg" aria-hidden="true">{initials}</span>
          <div className="m-account-text">
            <div className="m-account-name">{name}</div>
          </div>
        </div>
        <div className="m-section">
          <button type="button" className="m-row" onClick={() => navigate('/portal/account')}>
            <span className="m-row-icon"><i className="fas fa-user" aria-hidden="true"></i></span>
            <span className="m-row-label">My Account</span>
            <i className="fas fa-chevron-right m-row-chevron" aria-hidden="true"></i>
          </button>
          <button type="button" className="m-row m-row-danger" onClick={onLogout}>
            <span className="m-row-icon"><i className="fas fa-right-from-bracket" aria-hidden="true"></i></span>
            <span className="m-row-label">Log out</span>
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
